import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api, type OpenedView } from '../api/client';
import { AvisoError } from '../components/Aviso';
import { BarraDeCarga } from '../components/BarraDeCarga';
import { EstadoDeLaPagina, type Cronometro } from '../components/EstadoDeLaPagina';
import { SelectorDePagina } from '../components/SelectorDePagina';
import { PaginasContinuas, cargarImagen, type Objetivo } from '../components/PaginasContinuas';
import { primeraSinLeer } from '../lecturas/paginas';
import { crearDetectorDeCapturas, type EventoParaCapturas, type SenalDeCaptura } from '../lecturas/capturas';
import { IconoEscudo } from '../components/Iconos';
import { claveVisible } from '../documentos/clave';

/** Los mismos cuatro niveles que la API cachea. Un zoom continuo no existe aquí. */
const ZOOMS = [1, 2, 3, 4] as const;

/**
 * Lo que pide una página si la vista no trae su tiempo. Es lo de un escaneo
 * (ADR-0028); el servidor decide de todos modos.
 */
const SEGUNDOS_DE_RESPALDO = 30;

/**
 * Una ráfaga de teclas es un intento, no varios: dentro de este rato se avisa
 * otra vez en pantalla, pero a la bitácora va uno.
 */
const AGRUPAR_CAPTURAS_MS = 5_000;

/** Se renueva la concesión bastante antes de que caduque, no al filo. */
const RENOVAR_A_LOS_MS = 40_000;

/**
 * Cuánto esperar, tras cambiar de página, antes de guardar «aquí me quedé». Pasar
 * páginas con la flecha manda una sola marca al final, no una por página.
 */
const GUARDAR_POSICION_MS = 1_200;

/**
 * Ancho aproximado en píxeles de una carta a cada nivel, para elegir con qué
 * nivel abrir. Sirven para escoger, no para dibujar: la medida real viene en la
 * imagen que manda el servidor.
 */
export const ANCHO_APROXIMADO: Record<number, number> = { 1: 816, 2: 1224, 3: 1700, 4: 2448 };

/**
 * El nivel más parecido al ancho disponible, no el primero que lo cubra: pasarse
 * un nivel cuesta el doble de píxeles por una ganancia que nadie ve.
 */
function nivelQueLlena(anchoDisponible: number): number {
  return ZOOMS.reduce((mejor, nivel) =>
    Math.abs(ANCHO_APROXIMADO[nivel]! - anchoDisponible) <
    Math.abs(ANCHO_APROXIMADO[mejor]! - anchoDisponible)
      ? nivel
      : mejor,
  );
}

interface Props {
  documentId: string;
  /** Versión concreta. Sin ella se abre la vigente. */
  versionId?: string | undefined;
  onCerrar: () => void;
}

/**
 * Visor de sólo lectura.
 *
 * Cada página llega del servidor ya rasterizada y con la marca de agua **dentro
 * de la imagen**, y se pinta en un canvas: no hay capa de texto que seleccionar
 * ni PDF que guardar. El reloj de lectura sólo corre con la pestaña a la vista.
 *
 * **Lectura efectiva.** Una página cuenta como leída cuando estuvo a la vista,
 * con la pestaña visible, el tiempo que pide su texto y una a la vez —lo
 * decide el servidor, ADR-0028—; saltar de la 1 a la 100
 * deja leídas la 1 y la 100, no las de en medio. El acuse se habilita cuando
 * todas pasaron por la pantalla, y el servidor lo vuelve a comprobar.
 *
 * Al abrir, sigue donde la persona se quedó la última vez. En pantalla completa
 * las páginas van una debajo de otra y se cargan conforme se desplaza.
 */
export function Visor({ documentId, versionId, onCerrar }: Props) {
  const { t } = useTranslation();
  const [vista, setVista] = useState<OpenedView | null>(null);
  const [concesion, setConcesion] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [zoom, setZoom] = useState<number>(() =>
    nivelQueLlena(typeof window === 'undefined' ? 1200 : window.innerWidth - 260),
  );
  /** La página a la vista ya está dibujada: desde ahí corre su tiempo de lectura. */
  const [lista, setLista] = useState(false);
  /** Cuánto lleva bajada la página: `null` mientras el servidor la prepara. */
  const [avance, setAvance] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [leidas, setLeidas] = useState<ReadonlySet<number>>(new Set());
  /** La página en la que se retomó la lectura, para decirlo una vez. */
  const [retomada, setRetomada] = useState<number | null>(null);
  /** A dónde desplazarse en la vista continua; el contador distingue dos pedidos iguales. */
  const [objetivo, setObjetivo] = useState<Objetivo>({ pagina: 1, vez: 0 });
  const [acusando, setAcusando] = useState(false);
  const [acusado, setAcusado] = useState(false);
  /** Cuánto le falta a la página a la vista, para enseñarlo mientras corre. */
  const [cronometro, setCronometro] = useState<Cronometro | null>(null);
  /** La última página que el servidor dio por leída: se anuncia una vez. */
  const [recienLeida, setRecienLeida] = useState<number | null>(null);
  /** Un posible intento de captura que se acaba de ver (ADR-0029); `vez` reinicia el aviso. */
  const [captura, setCaptura] = useState<{ senal: SenalDeCaptura; vez: number } | null>(null);

  const marco = useRef<HTMLDivElement>(null);
  const lienzoRef = useRef<HTMLDivElement>(null);
  const lienzo = useRef<HTMLCanvasElement>(null);
  const concesionRef = useRef<string | null>(null);
  const vistaRef = useRef<OpenedView | null>(null);
  const leidasRef = useRef<ReadonlySet<number>>(leidas);
  /** Momento del último tiempo acreditado, para no perder el tramo final. */
  const ultimoCorte = useRef<number>(Date.now());
  /** Cuándo se abrió el visor, para el tiempo que acompaña al acuse. */
  const inicio = useRef<number>(Date.now());

  const paginaRef = useRef(pagina);

  concesionRef.current = concesion;
  vistaRef.current = vista;
  leidasRef.current = leidas;
  paginaRef.current = pagina;

  const total = vista?.pageCount ?? 0;

  // Abrir la vista. El identificador de la concesión es lo único que viaja: la
  // ruta del archivo en el almacén no sale nunca de la API.
  useEffect(() => {
    let vigente = true;
    api
      .openView(documentId, versionId)
      .then((abierta) => {
        if (!vigente) return;
        setVista(abierta);
        setConcesion(abierta.jti);
        setLeidas(new Set(abierta.pagesRead));
        // Donde se quedó la última vez. La primera vez, la página 1.
        setPagina(abierta.lastPage);
        setObjetivo({ pagina: abierta.lastPage, vez: 1 });
        if (abierta.lastPage > 1) setRetomada(abierta.lastPage);
      })
      .catch(setError);
    return () => {
      vigente = false;
    };
  }, [documentId, versionId]);

  // Al salir se cierra la sesión de lectura y se revoca la concesión. Si el
  // navegador se va sin avisar, el servidor la cierra solo por falta de latidos.
  useEffect(() => {
    return () => {
      const grantId = concesionRef.current;
      if (!grantId) return;
      // El tramo desde el último latido viaja en el cierre. Una lectura más
      // corta que el intervalo del latido quedaría, si no, registrada en cero.
      const pendientes = Math.round((Date.now() - ultimoCorte.current) / 1000);
      void api.closeView(grantId, 'CLOSED', pendientes).catch(() => {});
    };
  }, []);

  // Renovación de la concesión mientras la lectura sigue viva.
  useEffect(() => {
    if (!concesion) return;
    const temporizador = window.setTimeout(() => {
      api
        .renewGrant(concesion)
        .then((nueva) => setConcesion(nueva.jti))
        .catch(setError);
    }, RENOVAR_A_LOS_MS);
    return () => window.clearTimeout(temporizador);
  }, [concesion]);

  /**
   * Latido. Sin él no corre el reloj, y no se manda con la pestaña oculta: tener
   * la ventana minimizada no es leer.
   */
  useEffect(() => {
    if (!vista) return;
    const cada = vista.heartbeatSeconds * 1000;
    ultimoCorte.current = Date.now();

    const temporizador = window.setInterval(() => {
      const grantId = concesionRef.current;
      if (!grantId) return;
      if (document.visibilityState !== 'visible') {
        // La pestaña oculta no suma, pero sí mueve el corte: al volver no se
        // acredita el rato que la persona estuvo en otra cosa.
        ultimoCorte.current = Date.now();
        return;
      }
      const transcurridos = Math.round((Date.now() - ultimoCorte.current) / 1000);
      ultimoCorte.current = Date.now();
      void api.heartbeat(grantId, transcurridos, false).catch(() => {});
    }, cada);

    return () => window.clearInterval(temporizador);
  }, [vista]);

  // Vista por página: se dibuja la que toca. La imagen se libera en cuanto está
  // pintada: no queda un blob del documento colgando en el navegador. Si se
  // cambia de página antes de que llegue, la petición anterior se cancela.
  useEffect(() => {
    const grantId = concesionRef.current;
    if (!grantId || !vista || pantallaCompleta) return;
    const cancelar = new AbortController();
    setLista(false);
    setAvance(null);
    (async () => {
      try {
        const blob = await api.viewerPage(grantId, pagina, zoom, setAvance, cancelar.signal);
        const url = URL.createObjectURL(blob);
        try {
          const imagen = await cargarImagen(url);
          const canvas = lienzo.current;
          if (!canvas || cancelar.signal.aborted) return;
          canvas.width = imagen.naturalWidth;
          canvas.height = imagen.naturalHeight;
          canvas.getContext('2d')?.drawImage(imagen, 0, 0);
        } finally {
          URL.revokeObjectURL(url);
        }
        if (cancelar.signal.aborted) return;
        setLista(true);
        setError(null);
        void api.heartbeat(grantId, 0, true).catch(() => {});
      } catch (fallo) {
        if (!cancelar.signal.aborted) setError(fallo);
      }
    })();
    return () => cancelar.abort();
    // `concesion` no va en las dependencias a propósito: se renueva cada 40 s y
    // volver a pedir la página en cada renovación la haría parpadear. Se lee la
    // vigente del ref en el momento de pedir.
  }, [vista, pagina, zoom, pantallaCompleta]);

  /**
   * El tiempo de lectura de la página a la vista: el que pide su texto
   * (ADR-0028). Corre sólo con la pestaña visible y la página dibujada; si la
   * persona se va a otra pestaña, se pausa y al volver sigue desde donde iba.
   *
   * Al cumplirse se le avisa al servidor, que es quien decide: la página se da
   * por leída **cuando él contesta que sí**, no antes. Si contesta que falta
   * —la red, o un reloj que no coincide—, se espera lo que dice y se vuelve a
   * intentar.
   */
  useEffect(() => {
    if (!vista || !lista || leidasRef.current.has(pagina)) {
      setCronometro(null);
      return;
    }
    const totalMs = (vista.pageSeconds[pagina - 1] ?? SEGUNDOS_DE_RESPALDO) * 1000;
    let restante = totalMs;
    let desde = Date.now();
    let temporizador: number | undefined;
    let vigente = true;

    const marcar = () => {
      const grantId = concesionRef.current;
      if (!grantId) return;
      setCronometro({ pagina, totalMs, restanteMs: 0, estado: 'registrando' });
      api
        .markPage(grantId, pagina, true)
        .then(() => {
          // Ya quedó en el servidor: se cuenta aunque la persona haya seguido.
          setLeidas((anteriores) => new Set(anteriores).add(pagina));
          if (vigente) setRecienLeida(pagina);
        })
        .catch((fallo: unknown) => {
          if (!vigente) return;
          const espera = fallo instanceof ApiError && fallo.code === 'reading.page_too_soon'
            ? Number(fallo.params?.['waitSeconds'])
            : NaN;
          if (Number.isFinite(espera) && espera > 0) {
            restante = espera * 1000;
            programar();
          } else {
            setCronometro(null);
          }
        });
    };
    const programar = () => {
      desde = Date.now();
      temporizador = window.setTimeout(marcar, restante);
      setCronometro({ pagina, totalMs, restanteMs: restante, estado: 'corriendo', desde });
    };
    const pausar = () => {
      window.clearTimeout(temporizador);
      restante = Math.max(0, restante - (Date.now() - desde));
      setCronometro({ pagina, totalMs, restanteMs: restante, estado: 'pausa' });
    };
    const alCambiarVisibilidad = () => {
      if (document.visibilityState === 'visible') programar();
      else pausar();
    };

    if (document.visibilityState === 'visible') programar();
    else pausar();
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      vigente = false;
      window.clearTimeout(temporizador);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [vista, lista, pagina]);

  // En la vista continua no hay «dibujar la página»: cuenta como vista cuando
  // queda a la vista ya dibujada, igual que en la vista por página.
  useEffect(() => {
    const grantId = concesionRef.current;
    if (!pantallaCompleta || !lista || !grantId) return;
    void api.heartbeat(grantId, 0, true).catch(() => {});
  }, [pantallaCompleta, lista, pagina]);

  // «Aquí me quedé»: se guarda al detenerse en una página, no en cada paso.
  useEffect(() => {
    if (!vista) return;
    const temporizador = window.setTimeout(() => {
      const grantId = concesionRef.current;
      if (grantId) void api.markPage(grantId, pagina, false).catch(() => {});
    }, GUARDAR_POSICION_MS);
    return () => window.clearTimeout(temporizador);
  }, [vista, pagina]);

  /** Ir a una página: en la vista por página se cambia; en la continua, se desplaza hasta ella. */
  const irA = useCallback(
    (numero: number) => {
      const destino = Math.min(Math.max(1, numero), total || 1);
      setRetomada(null);
      setPagina(destino);
      setObjetivo((anterior) => ({ pagina: destino, vez: anterior.vez + 1 }));
    },
    [total],
  );

  /**
   * Pantalla completa de verdad, la del navegador: el documento se queda con
   * toda la pantalla y los controles siguen arriba.
   */
  const alternarPantallaCompleta = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await marco.current?.requestFullscreen();
    } catch {
      // Un navegador puede negarla; no es motivo para romper la lectura.
    }
  }, []);

  useEffect(() => {
    const alCambiar = () => {
      const completa = Boolean(document.fullscreenElement);
      setPantallaCompleta(completa);
      setLista(false);
      // La vista continua arranca en la página en la que se estaba.
      setObjetivo((anterior) => ({ pagina: anterior.pagina, vez: anterior.vez + 1 }));
    };
    document.addEventListener('fullscreenchange', alCambiar);
    return () => document.removeEventListener('fullscreenchange', alCambiar);
  }, []);

  /**
   * Posibles capturas de pantalla (ADR-0029). No se bloquean —el navegador no
   * deja—: se le dice a la persona en ese momento y queda en la bitácora con la
   * página. Se escucha en la fase de captura para enterarse aunque un campo
   * tenga el foco.
   */
  useEffect(() => {
    if (!vista) return;
    const detectar = crearDetectorDeCapturas();
    let ultimaReportada = 0;
    const atender = (evento: EventoParaCapturas) => {
      const senal = detectar(evento);
      if (!senal) return;
      const ahora = Date.now();
      setCaptura({ senal, vez: ahora });
      if (ahora - ultimaReportada < AGRUPAR_CAPTURAS_MS) return;
      ultimaReportada = ahora;
      const grantId = concesionRef.current;
      if (grantId) void api.reportCapture(grantId, paginaRef.current, senal).catch(() => {});
    };
    const alTeclear = (e: KeyboardEvent) =>
      atender({ tipo: e.type === 'keyup' ? 'keyup' : 'keydown', key: e.key, metaKey: e.metaKey, shiftKey: e.shiftKey });
    const alMoverse = (e: PointerEvent) => atender({ tipo: 'puntero', metaKey: e.metaKey, shiftKey: e.shiftKey });
    const alPerderFoco = () => atender({ tipo: 'blur' });
    window.addEventListener('keydown', alTeclear, true);
    window.addEventListener('keyup', alTeclear, true);
    window.addEventListener('pointermove', alMoverse, true);
    window.addEventListener('blur', alPerderFoco);
    return () => {
      window.removeEventListener('keydown', alTeclear, true);
      window.removeEventListener('keyup', alTeclear, true);
      window.removeEventListener('pointermove', alMoverse, true);
      window.removeEventListener('blur', alPerderFoco);
    };
  }, [vista]);

  // Teclado: pasar páginas y salir. En pantalla completa es la única vía cómoda.
  // Mientras se escribe en un campo —el de la página— las teclas son del campo.
  useEffect(() => {
    const alTeclear = (evento: KeyboardEvent) => {
      const destino = evento.target as HTMLElement | null;
      if (destino && /^(INPUT|TEXTAREA|SELECT)$/.test(destino.tagName)) return;
      if (evento.key === 'ArrowRight' || evento.key === 'PageDown') {
        evento.preventDefault();
        irA(pagina + 1);
      } else if (evento.key === 'ArrowLeft' || evento.key === 'PageUp') {
        evento.preventDefault();
        irA(pagina - 1);
      } else if (evento.key === 'f' || evento.key === 'F') {
        void alternarPantallaCompleta();
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [pagina, irA, alternarPantallaCompleta]);

  /**
   * El acuse de lectura.
   *
   * Manda el tiempo que el visor acreditó —el de la pestaña visible, no el de
   * reloj— como **dato** del acuse, no como condición: quien ya conocía el
   * documento no tiene por qué fingir que lo lee otra vez. Sirve para que quien
   * audite distinga un acuse de una lectura.
   */
  const acusar = useCallback(async () => {
    if (!vistaRef.current) return;
    setAcusando(true);
    setError(null);
    try {
      const transcurridos = Math.round((Date.now() - inicio.current) / 1000);
      await api.acknowledge(
        vistaRef.current.documentId,
        vistaRef.current.versionId,
        Math.min(transcurridos, 86_400),
      );
      setAcusado(true);
    } catch (fallo) {
      setError(fallo);
    } finally {
      setAcusando(false);
    }
  }, []);

  /**
   * Imprimir es un permiso, no un atajo del navegador. El PDF lo arma el
   * servidor con las mismas páginas marcadas —el papel sale con el nombre de
   * quien lo imprimió— y el original nunca se toca. Se abre en un marco oculto
   * para que el diálogo de impresión sea el del propio PDF.
   */
  const imprimir = useCallback(async () => {
    const grantId = concesionRef.current;
    if (!grantId) return;
    setImprimiendo(true);
    let url: string | null = null;
    let marcoImpresion: HTMLIFrameElement | null = null;
    try {
      const blob = await api.printablePdf(grantId);
      url = URL.createObjectURL(blob);

      marcoImpresion = document.createElement('iframe');
      marcoImpresion.style.display = 'none';
      marcoImpresion.src = url;
      document.body.appendChild(marcoImpresion);
      await new Promise((listo) => {
        marcoImpresion!.onload = () => listo(null);
      });
      marcoImpresion.contentWindow?.print();
    } catch (fallo) {
      setError(fallo);
      if (marcoImpresion) marcoImpresion.remove();
      if (url) URL.revokeObjectURL(url);
    } finally {
      setImprimiendo(false);
    }
    // El marco y su URL no se liberan aquí a propósito: mientras el diálogo de
    // impresión sigue abierto, soltarlos deja al navegador sin qué imprimir.
  }, []);

  const leidasDelTotal = [...leidas].filter((n) => n >= 1 && n <= total).length;
  const lecturaCompleta = total > 0 && leidasDelTotal >= total;
  const faltante = vista ? primeraSinLeer(leidas, total) : null;
  const pidiendoAcuse = Boolean(vista?.mandatoryReading && !vista.acknowledged && !acusado);
  const cargandoPagina = !pantallaCompleta && Boolean(vista) && !lista;

  return (
    <div className={`visor${pantallaCompleta ? ' visor--completa' : ''}`} ref={marco}>
      <div className="visor__barra">
        <div className="visor__identidad">
          <span className="mono">{vista ? claveVisible(vista) : '—'}</span>
          <strong>{vista?.title ?? t('viewer.loading')}</strong>
          {vista && (
            <span
              className={`pildora pildora--${
                vista.isExpiredDocument ? 'critico' : vista.isCurrentVersion ? 'bueno' : 'aviso'
              }`}
            >
              <span className="pildora__punto" aria-hidden="true" />
              {vista.isExpiredDocument
                ? t('viewer.expired')
                : t('documents.versionShort', { number: vista.versionLabel })}
            </span>
          )}
        </div>

        <div className="visor__mandos">
          <div className="visor__paginacion">
            <button
              className="boton boton--discreto"
              type="button"
              disabled={pagina <= 1 || !vista}
              onClick={() => irA(pagina - 1)}
            >
              {t('viewer.previous')}
            </button>
            <SelectorDePagina
              pagina={pagina}
              total={total}
              deshabilitado={!vista || total <= 1}
              onIr={irA}
            />
            <button
              className="boton boton--discreto"
              type="button"
              disabled={pagina >= total || !vista}
              onClick={() => irA(pagina + 1)}
            >
              {t('viewer.next')}
            </button>
          </div>

          <div className="visor__zoom" role="group" aria-label={t('viewer.zoom')}>
            {ZOOMS.map((nivel) => (
              <button
                key={nivel}
                type="button"
                className={`visor__nivel${nivel === zoom ? ' visor__nivel--activo' : ''}`}
                aria-pressed={nivel === zoom}
                disabled={cargandoPagina}
                onClick={() => setZoom(nivel)}
              >
                {t('viewer.zoomLevel', { level: nivel })}
              </button>
            ))}
          </div>

          <button
            className="boton boton--discreto"
            type="button"
            onClick={() => void alternarPantallaCompleta()}
            aria-pressed={pantallaCompleta}
          >
            {pantallaCompleta ? t('viewer.exitFullscreen') : t('viewer.fullscreen')}
          </button>

          {vista?.canPrint && (
            <button
              className="boton boton--discreto"
              type="button"
              disabled={imprimiendo}
              onClick={() => void imprimir()}
            >
              {imprimiendo ? t('viewer.preparingPrint') : t('viewer.print')}
            </button>
          )}

          <button className="boton boton--fantasma" type="button" onClick={onCerrar}>
            {t('viewer.close')}
          </button>
        </div>

        {/* Cuánto lleva leído, y el acuse cuando el documento lo pide. La cuenta
            es de páginas que de verdad estuvieron a la vista, no de hasta dónde
            se llegó. */}
        {vista && vista.mandatoryReading && (
          <div className="visor__lectura">
            <span className="visor__lectura-avance">
              <span
                className="visor__lectura-barra"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={leidasDelTotal}
                aria-label={t('viewer.pagesReadOf', { read: leidasDelTotal, total })}
              >
                <span style={{ width: `${total ? (leidasDelTotal / total) * 100 : 0}%` }} />
              </span>
              <span className="visor__lectura-texto">
                {t('viewer.pagesReadOf', { read: leidasDelTotal, total })}
              </span>
            </span>

            {pidiendoAcuse && (
              <EstadoDeLaPagina
                pagina={pagina}
                leida={leidas.has(pagina)}
                recien={recienLeida === pagina}
                cronometro={cronometro}
              />
            )}

            {pidiendoAcuse && !lecturaCompleta && faltante !== null && (
              <button
                className="boton boton--discreto"
                type="button"
                onClick={() => irA(faltante)}
                title={t('viewer.nextUnreadHelp')}
              >
                {t('viewer.nextUnread', { page: faltante })}
              </button>
            )}

            {pidiendoAcuse && (
              <button
                className="boton"
                type="button"
                disabled={acusando || !lecturaCompleta}
                title={
                  lecturaCompleta
                    ? undefined
                    : t('reading.acknowledgeLocked', { missing: total - leidasDelTotal })
                }
                onClick={() => void acusar()}
              >
                {acusando ? t('reading.acknowledging') : t('reading.acknowledge')}
              </button>
            )}

            {(vista.acknowledged || acusado) && (
              <span className="pildora pildora--bueno">
                <span className="pildora__punto" aria-hidden="true" />
                {t('reading.acknowledged')}
              </span>
            )}
          </div>
        )}
      </div>

      <AvisoError error={error} />

      {/* No bloquea: avisa, en grande y en ese momento, que quedó registrado. */}
      {captura && (
        <div key={captura.vez} className="visor__captura" role="alert">
          <span className="visor__captura-sello" aria-hidden="true">
            <IconoEscudo />
          </span>
          <div className="visor__captura-texto">
            <strong>{t('viewer.capture.title')}</strong>
            <span>{t('viewer.capture.body', { page: pagina })}</span>
          </div>
          <button className="boton" type="button" onClick={() => setCaptura(null)}>
            {t('viewer.capture.ok')}
          </button>
        </div>
      )}

      {retomada !== null && (
        <p className="visor__retomada" role="status">
          {t('viewer.resumed', { page: retomada })}
          <button className="boton boton--discreto" type="button" onClick={() => irA(1)}>
            {t('viewer.fromStart')}
          </button>
        </p>
      )}

      {/* Quien consulta una versión superada tiene que saberlo antes de leerla:
          es la diferencia entre consultar la norma y consultar su historia. */}
      {vista && !vista.isCurrentVersion && !vista.isExpiredDocument && (
        <p className="visor__historica">
          {t('viewer.notCurrentVersion', { number: vista.versionLabel })}
        </p>
      )}

      <div
        className={`visor__lienzo${pantallaCompleta ? ' visor__lienzo--continuo' : ''}`}
        ref={lienzoRef}
      >
        {!vista && !error && <BarraDeCarga avance={null} texto={t('viewer.opening')} />}

        {vista && pantallaCompleta && (
          <PaginasContinuas
            raiz={lienzoRef}
            concesion={concesionRef}
            total={total}
            zoom={zoom}
            anchoMaximo={ANCHO_APROXIMADO[zoom] ?? 1224}
            objetivo={objetivo}
            leidas={leidas}
            onActual={(numero, dibujada) => {
              setPagina(numero);
              setLista(dibujada);
            }}
            onError={setError}
          />
        )}

        {vista && !pantallaCompleta && (
          <>
            {cargandoPagina && (
              <BarraDeCarga avance={avance} texto={t('viewer.rendering')} flotante />
            )}
            {/* Sin menú contextual: no evita una captura, evita el "guardar
                imagen" de un clic derecho, que es el camino que cualquiera toma
                primero. */}
            <canvas
              ref={lienzo}
              className={`visor__pagina${cargandoPagina ? ' visor__pagina--cargando' : ''}`}
              onContextMenu={(evento) => evento.preventDefault()}
              aria-label={t('viewer.pageOf', { page: pagina, total })}
            />
          </>
        )}
      </div>

      {/* Lo que se lleva el papel cuando alguien imprime con Ctrl+P: el visor no
          es imprimible desde el navegador, el imprimible lo emite la API. */}
      <p className="visor__sin-impresion">{t('viewer.printNotAllowed')}</p>

      {/* El límite real del control, escrito donde se lee y no sólo en un ADR. */}
      <p className="visor__limite">{t('viewer.limitNotice')}</p>
    </div>
  );
}
